// @ts-strict-ignore
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import {PropertyType, ResourceType} from 'common/report/constants';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {INSIGHTS_GROUP_PROPERTIES} from 'common/report/insights/models/constants';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import {getEventValueAndLabel} from 'common/report/util/events';
import {assert} from 'common/util';
import {assertUnreachable} from 'common/util/typescript';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {FiltersOperator, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {TrackName as DataTableTrackName} from 'common/components/data-table/types';
import {
  FilterClause as FilterClauseType,
  FilterDeterminer,
  GroupClause as GroupClauseType,
  MetricBehavior,
} from 'common/report/insights/util/types';
import {ChartType, InsightsResourceType, MetricType} from 'common/types/reports/bookmark';
import {parseDateUnitFromRange, parseNumber, parseNumberRange} from 'common/report/util/sorting/header-sorting';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {parseDatetimeRange} from 'common/util/date';
import {INSIGHTS_DATE_RANGE_NEGATION_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {
  BOOLEAN_OPERATOR_NEGATION_MAP,
  NUMBER_OPERATOR_NEGATION_MAP,
  STRING_OPERATOR_NEGATION_MAP,
} from 'common/report/insights/util';
import {
  BooleanOperator,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {ConstantPropertyValues} from 'common/report/util/properties';
import {isMultiAttributionBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {
  BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
  RECURRING_REVENUE_CHANGE_TYPE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {DisplayContext as PropertyFilterDisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {
  createFirstTimeInPeriodEventFilter,
  createLastTimeInPeriodEventFilter,
} from 'common/widgets/property-filter-menu/models/utils';
import {getPropertyFilters} from 'common/report/insights/util/filters';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {REPLAY_RETENTION_DAYS, ReplaysDisabledReason} from 'common/widgets/recording-player/query-utils';
import {LocalizationStore} from 'common/i18n/store';

function determinerToFiltersOperator(determiner: FilterDeterminer) {
  if (!determiner) {
    return null;
  }
  switch (determiner) {
    case `any`:
      return FiltersOperator.Or;
    case `all`:
      return FiltersOperator.And;
    default:
      assertUnreachable(determiner);
  }
}

export function convertSegmentForEventsInspector(behaviorShowClauses: BehaviorShowClause[]): QueryEventEntry[] {
  return behaviorShowClauses.map((showClause) => {
    const showClauseFilters = showClause.behavior.filters || [];
    const determiner = showClauseFilters.find((filter: FilterClause) => filter.determiner)?.determiner;
    const filters: PropertyFilter[] = showClauseFilters.map((filter: FilterClause): PropertyFilter => {
      return new FilterClause(filter).toPropertyFilter();
    });
    const {name, id} = showClause.behavior;
    const custom = showClause.behavior.type === MetricType.CustomEvent;
    assert(!Array.isArray(name));
    const {label, value} = getEventValueAndLabel({name, custom, id});
    const queryEventEntry: QueryEventEntry = {
      type: ResourceType.Event,
      dataGroupId: showClause.measurement.dataGroupId || showClause.behavior.dataGroupId,
      event: {
        label,
        value,
        custom,
        id,
      },
      filters,
      filtersOperator: determinerToFiltersOperator(determiner),
    };
    return queryEventEntry;
  });
}

export function hasDataTable(chartType: ChartType) {
  return [
    ChartType.Column,
    ChartType.Line,
    ChartType.Metric,
    ChartType.Pie,
    ChartType.FunnelSteps,
    ChartType.RetentionCurve,
  ].includes(chartType);
}

export function getDataTableTrackName(chartType: ChartType) {
  switch (chartType) {
    case ChartType.Column:
      return DataTableTrackName.InsightsColumn;
    case ChartType.Line:
      return DataTableTrackName.InsightsLine;
    case ChartType.Metric:
      return DataTableTrackName.InsightsMetric;
    case ChartType.Pie:
      return DataTableTrackName.InsightsPie;
    case ChartType.FunnelSteps:
      return DataTableTrackName.FunnelsSteps;
    case ChartType.RetentionCurve:
      return DataTableTrackName.RetentionCurve;
    default:
      throw new Error(`Data table not supported for ${chartType}`);
  }
}

export function isNoneBucketing(groupClause) {
  if (groupClause.customBucket && `disabled` in groupClause.customBucket) {
    return groupClause.customBucket.disabled === true ? PropertyType.String : false;
  }

  return false;
}

export function getFunnelSegmentNames({behaviors, group}: {behaviors: MetricBehavior[]; group: GroupClauseType[]}) {
  const segmentNames = [];
  behaviors.forEach((beh, idx) => {
    if (beh.behaviors) {
      segmentNames.push(`Step ` + (idx + 1) + ` comparison`);
    }
  });
  return segmentNames.concat(
    group.map((groupClause) => {
      let segmentName;
      if (groupClause.cohorts) {
        segmentName = `Cohort`;
      } else {
        segmentName = groupClause.value;
      }
      return segmentName;
    }),
  );
}

export function createNumericFiltersFromGroup(groupClause: Partial<GroupClause>, filterValue) {
  const filter = createFilterFromGroup(groupClause, filterValue, true, null);
  if (filter.filterOperator === NumberOperator.Between) {
    const filters = [
      {
        ...filter,
        filterOperator: NumberOperator.AtLeast,
        filterValue: filter.filterValue[0],
      },
      {
        ...filter,
        filterOperator: NumberOperator.LessThan,
        filterValue: filter.filterValue[1],
      },
    ];
    return filters;
  }

  return [filter];
}

export function createFilterFromGroup(
  groupClause: Partial<GroupClause>,
  filterValue,
  includesFilterValue: boolean,
  timePeriodSettings,
) {
  const groupDataset = groupClause.dataset;
  const groupProperties = pick(groupClause, INSIGHTS_GROUP_PROPERTIES);
  groupProperties[`defaultType`] = groupClause.propertyType;
  if (groupClause.propertyFilter && groupClause.propertyFilter.propertyType !== groupClause[`propertyDefaultType`]) {
    groupProperties.typeCast = groupClause.propertyFilter.propertyType;
  }
  if (groupClause.value === `$time_to_conversion`) {
    groupProperties.startStepNumber = groupClause.startStepNumber;
    groupProperties.endStepNumber = groupClause.endStepNumber;
  }
  const joinType = groupClause.joinPropertyType;
  if (joinType) {
    groupProperties.filterJoinType = joinType;

    if (joinType === PropertyType.List) {
      groupProperties.listQuantifier = !includesFilterValue
        ? ListQuantifier.All
        : groupClause?.listQuantifier ?? ListQuantifier.Any;
    }
  }

  if (isMultiAttributionBehavior(groupClause.behavior)) {
    return createMultiAttributionFilter(groupClause, filterValue, includesFilterValue);
  }

  // None bucketing must evaluate to a String property type
  let groupType =
    isNoneBucketing(groupClause) ||
    groupProperties.typeCast ||
    groupProperties.propertyType ||
    groupProperties.customProperty?.propertyType;

  // filterJoinType is set for list, object, list of objects, and lookup table props
  // groupType should be overriden for list, object, and list of objects props
  if (!groupProperties.typeCast && groupProperties.filterJoinType) {
    if (groupProperties.filterJoinType === PropertyType.List && groupProperties.propertyType === PropertyType.Object) {
      // if it's a list of object, set groupType to "object"
      groupType = PropertyType.Object;
    } else if (!Array.isArray(groupProperties.value)) {
      // if it's a list or object prop and not a lookup table prop, set groupType to groupProperties.filterJoinType
      groupType = groupProperties.filterJoinType;
    }
  }

  if ([`undefined`].includes(filterValue)) {
    if (groupType === `number`) {
      return createUndefinedFilter(groupDataset, groupProperties, includesFilterValue, `number`);
    }

    return createStringFilter(groupDataset, groupProperties, `undefined`, includesFilterValue, `string`);
  }

  switch (groupType) {
    case `string`:
      return createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
    case `boolean`:
      return createBooleanFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
    case `list`:
      // We cannot use list selectors to filter out empty lists, since they apply element-wise
      // Cast list property to string and do the comparison with $empty_list
      // query-api correctly resolves $empty_list to the right value
      if (filterValue === ConstantPropertyValues.EmptyList) {
        return createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      } else {
        return createListFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      }
    case `number`: {
      if (groupClause.value === `$time_to_conversion`) {
        // time to convert breakdowns are modeled using numeric breakdowns but formatted as dates
        // this attempts to parse dates only for this property
        const parsedDateUnit = parseDateUnitFromRange(filterValue);
        if (parsedDateUnit) {
          groupProperties.filterDateUnit = parsedDateUnit;
        }
      }
      return createNumberFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
    }
    case `object`:
      if (groupClause.listItemGroup) {
        return createListOfObjectsFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      }
      return createObjectFilter(
        groupDataset,
        groupProperties,
        filterValue,
        includesFilterValue,
        groupClause,
        timePeriodSettings,
      );
    case `datetime`:
      if ([`day_of_week`, `hour_of_day`].includes(groupProperties.unit)) {
        const filter = createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
        filter[`filterDateUnit`] = groupProperties.unit;
        return filter;
      } else {
        return createDatetimeFilter(
          groupDataset,
          groupProperties,
          filterValue,
          timePeriodSettings,
          includesFilterValue,
          groupClause,
        );
      }
  }

  return null;
}

export function createUndefinedFilter(groupDataset, groupProperties, includesFilterValue, filterType = `string`) {
  return {
    ...groupProperties,
    filterOperator: includesFilterValue ? `is not set` : `is set`,
    filterType,
    dataset: groupDataset,
  };
}

export function createStringFilter(
  groupDataset,
  groupProperties,
  filterValue,
  includesFilterValue,
  groupClause = null,
) {
  let filterOperator = includesFilterValue ? `equals` : `does not equal`;

  if (groupClause?.filterOperator) {
    filterOperator = includesFilterValue
      ? groupClause.filterOperator
      : STRING_OPERATOR_NEGATION_MAP[groupClause.filterOperator];
  }

  if (!Array.isArray(filterValue)) {
    filterValue = [filterValue];
  }

  return {
    ...groupProperties,
    filterJoinType: null,
    listQuantifier: null,
    filterOperator,
    filterType: `string`,
    filterValue: groupClause?.filterValue ?? filterValue,
    dataset: groupDataset,
  };
}

export function createMultiAttributionFilter(
  groupClause: Partial<GroupClause>,
  filterValue: string,
  includesFilterValue: boolean,
) {
  let filterOperator = includesFilterValue ? StringOperator.Equals : StringOperator.DoesNotEqual;

  const intermediateResult = {
    ...groupClause,
    filterOperator,
    filterType: PropertyType.String,
    filterValue,
    value: BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value,
  };

  // Multi-attribution properties are string types. When custom bucketing, you can create buckets based on Is(equals) or contains
  // conditions. If using an `equals`, the condition is against one or more values, so treat the filter value as an array.
  // However, if using a `contains`, the condition will always be a substring check on a single value, so don't treat is an array.
  // DQS will error out if `contains` is used, as is, for an array.
  if (groupClause?.filterOperator === `contains`) {
    filterOperator = includesFilterValue
      ? groupClause.filterOperator
      : STRING_OPERATOR_NEGATION_MAP[groupClause.filterOperator];

    return {
      ...intermediateResult,
      filterOperator,
    };
  }

  return {
    ...intermediateResult,
    filterJoinType: PropertyType.List,
    filterValue: Array.isArray(filterValue) ? [...filterValue] : [filterValue],
    listQuantifier: includesFilterValue ? ListQuantifier.Any : ListQuantifier.All,
  };
}

export function createBooleanFilter(
  groupDataset,
  groupProperties,
  filterValue,
  includesFilterValue,
  groupClause = null,
) {
  // Value Comparison case
  if (groupClause?.filterOperator) {
    return {
      ...groupProperties,
      filterOperator: includesFilterValue
        ? groupClause.filterOperator
        : BOOLEAN_OPERATOR_NEGATION_MAP[groupClause.filterOperator],
      filterType: `boolean`,
      dataset: groupDataset,
    };
  }

  const isTrue = !!JSON.parse(filterValue) === includesFilterValue;
  return {
    ...groupProperties,
    filterOperator: isTrue ? `is true` : `is false`,
    filterType: `boolean`,
    dataset: groupDataset,
  };
}

export function createListFilter(groupDataset, groupProperties, filterValue, includesFilterValue, _groupClause = null) {
  const filter = {
    ...groupProperties,
    dataset: groupDataset,
    filterValue,
    filterType: `list`,
    filterOperator: includesFilterValue ? `contains` : `does not contain`,
  };

  return filter;
}

export function createNumberFilter(groupDataset, groupProperties, value, includesFilterValue, groupClause = null) {
  // Value Comparison case
  if (groupClause?.filterOperator && (groupClause?.filterValue || groupClause?.filterValue === 0)) {
    return {
      ...groupProperties,
      filterType: `number`,
      dataset: groupDataset,
      filterOperator: includesFilterValue
        ? groupClause.filterOperator
        : NUMBER_OPERATOR_NEGATION_MAP[groupClause.filterOperator],
      filterValue: groupClause.filterValue,
    };
  }
  const {filterOperator, filterValue} = assignNumericFilterOperatorAndValue(
    value,
    includesFilterValue,
    groupClause?.behavior,
  );

  return {
    ...groupProperties,
    filterType: `number`,
    dataset: groupDataset,
    filterOperator,
    filterValue,
  };
}

export function createObjectFilter(
  groupDataset: string,
  groupProperties: Partial<GroupClause>,
  filterValue: TODO,
  includesFilterValue: boolean,
  groupClause: Partial<GroupClause>,
  timePeriodSettings: {quarter_start_month: number},
) {
  const filter = createFilterFromGroup(
    new GroupClause({
      ...groupClause,
      joinPropertyType: null,
      propertyObjectKey: null,
      resourceType: groupClause.resourceType,
      value: groupClause.value,
    }),
    filterValue,
    includesFilterValue,
    timePeriodSettings,
  );

  return {
    ...filter,
    filterJoinType: PropertyType.Object,
    propertyObjectKey: groupProperties.propertyObjectKey,
    dataset: groupDataset,
  };
}

export function createListOfObjectsFilter(
  groupDataset,
  groupProperties,
  filterValue,
  includesFilterValue,
  groupClause,
) {
  // group is a list of objects property breakdown
  const listItemFilter = createFilterFromGroup(
    new GroupClause({
      ...groupClause.listItemGroup,
      resourceType: groupClause.resourceType,
      value: groupClause.listItemGroup.propertyName,
    }),
    filterValue,
    includesFilterValue,
    `a`,
  );

  // list of objects filter is most like a boolean filter
  const filter = createBooleanFilter(groupDataset, groupProperties, true, true, groupClause);
  filter.filterType = PropertyType.Object;
  filter.filterOperator = BooleanOperator.True;
  filter.listItemFilters = [omit(listItemFilter, [`propertyType`, `unit`])];
  filter.listQuantifier = includesFilterValue ? ListQuantifier.Any : ListQuantifier.All;

  return filter;
}

export function assignNumericFilterOperatorAndValue(value, includesFilterValue, isBehavior) {
  const numericValue = typeof value === `number` ? value : parseNumber(value);
  const numberRange = typeof value === `number` ? value : parseNumberRange(value);
  let filterOperator;
  let filterValue;

  if (!Array.isArray(numberRange) && numericValue === null) {
    return null;
  }

  if (numericValue !== null) {
    filterOperator = includesFilterValue ? NumberOperator.EqualTo : NumberOperator.NotEqualTo;
    filterValue = numericValue;
  } else {
    if (Number.isFinite(numberRange[0]) && Number.isFinite(numberRange[1])) {
      filterOperator = includesFilterValue ? NumberOperator.Between : NumberOperator.NotBetween;
      // SRFE-6711: Looking for "at least 0" causes issues when the default value is 0, so switch to "is greater than 0"
      numberRange[0] = includesFilterValue && isBehavior && numberRange[0] === 0 ? 1 : numberRange[0];
      filterValue = numberRange;
    } else if (Number.isFinite(numberRange[1])) {
      filterOperator = includesFilterValue ? NumberOperator.LessThan : NumberOperator.AtLeast;
      filterValue = numberRange[1];
    } else {
      filterOperator = includesFilterValue ? NumberOperator.AtLeast : NumberOperator.LessThan;
      filterValue = numberRange[0];
    }
  }

  return {filterOperator, filterValue};
}

export function createDatetimeFilter(
  groupDataset,
  groupProperties,
  filterValue,
  timePeriodSettings,
  includesFilterValue,
  groupClause = null,
) {
  const unit = groupProperties.unit;
  const quarterStartMonth =
    timePeriodSettings && timePeriodSettings.quarter_start_month ? timePeriodSettings.quarter_start_month : 0;
  const dateRange = parseDatetimeRange(filterValue, unit, quarterStartMonth);
  const defaultAndFilterType = `datetime`;

  let filterOperator;
  if (groupClause?.filterOperator) {
    filterOperator = includesFilterValue
      ? groupClause.filterOperator
      : INSIGHTS_DATE_RANGE_NEGATION_MAPPING[groupClause.filterOperator];
  }

  if (dateRange) {
    return {
      ...groupProperties,
      defaultType: groupClause?.defaultType ?? defaultAndFilterType,
      filterOperator: filterOperator ?? parseDatetimeFilterOperator(unit, includesFilterValue),
      filterType: groupClause?.filterType ?? defaultAndFilterType,
      filterValue: groupClause?.filterValue ?? dateRange,
      filterDateUnit: unit,
      dataset: groupDataset,
    };
  }

  return null;
}

function parseDatetimeFilterOperator(unit, includesFilterValue) {
  if (unit === `day`) {
    return includesFilterValue ? `was on` : `was not on`;
  } else {
    return includesFilterValue ? `was between` : `was not between`;
  }
}

export function getNthTimeInPeriodFilter(showClause: BehaviorShowClause): PropertyFilter {
  if (showClause.behavior.filters?.length > 0) {
    for (const filter of showClause.behavior.filters) {
      if (filter && filter.resourceType === `events` && filter.filterOperator === `nth time`) {
        // First Time Ever filter overrides Nth Time In Period Filters.
        return null;
      }
    }
  }

  let nthTimeInPeriodFilter: PropertyFilter = null;
  if (showClause.measurement.segmentMethod === `first`) {
    nthTimeInPeriodFilter = createFirstTimeInPeriodEventFilter({
      displayContext: PropertyFilterDisplayContext.Insights,
    });
  } else if (showClause.measurement.segmentMethod === `last`) {
    nthTimeInPeriodFilter = createLastTimeInPeriodEventFilter({
      displayContext: PropertyFilterDisplayContext.Insights,
    });
  }
  return nthTimeInPeriodFilter;
}

export function sortFiltersForCohort(
  subDayFilters: PropertyFilter[],
  nthTimeInPeriodFilter: PropertyFilter,
  segmentFilterClauses: FilterClause[],
  globalFilterClauses: FilterClause[],
): PropertyFilter[] {
  let combinedFilters: PropertyFilter[] = [...(subDayFilters ?? [])];
  if (globalFilterClauses?.length > 0) {
    combinedFilters = [...combinedFilters, ...getPropertyFilters(globalFilterClauses)];
  }

  // The nth time in period filters must come after the sub day period defining filters for logical accuracy.
  // The nth time in period filters also logically come after global (and inline) filters, since it filters to
  // the nth conversion for the query as its defined.
  if (nthTimeInPeriodFilter !== null) {
    combinedFilters.push(nthTimeInPeriodFilter);
  }
  // The nth time in period filter must come prior to segment filters otherwise we'd return the nth conversion
  // associated with the segment, not the nth conversion for the query as its defined.
  if (segmentFilterClauses?.length > 0) {
    combinedFilters = [...combinedFilters, ...getPropertyFilters(segmentFilterClauses)];
  }
  return combinedFilters;
}

export function getSelectedDateRange({
  date,
  unit,
  quarterStartMonth,
}: {
  date: Date | number | string;
  unit: TimeUnit;
  quarterStartMonth: number;
}) {
  const parsedDateRange = parseDatetimeRange(date, unit, quarterStartMonth);
  if (unit === TimeUnit.Day) {
    return {
      from: parsedDateRange,
      to: parsedDateRange,
      type: DateRangeType.On,
    };
  }
  return {
    from: parsedDateRange[0],
    to: parsedDateRange[1],
    type: DateRangeType.Between,
  };
}

export function isTimeSeriesChartType(chartType: ChartType) {
  return ![ChartType.Bar, ChartType.StackedBar, ChartType.Pie, ChartType.Table, ChartType.Metric].includes(chartType);
}

export function makeCohortFilter(cohort: Cohort): FilterClauseType {
  return {
    filterOperator: StringOperator.Contains,
    filterType: PropertyType.List,
    filterValue: [{cohort: {raw_cohort: cohort.serialize()}}],
    dataset: `$mixpanel`,
    resourceType: InsightsResourceType.Events,
    value: `$cohorts`,
    profileType: null,
    search: ``,
    dataGroupId: null,
  };
}

export function shouldHideCohortFunctionality(cohort: Cohort) {
  return cohort.groups.some((group) => {
    return group.filters?.some((filter) => {
      // handles event + rev filter, and SCD "any" filters
      if (filter.customProperty) {
        return filter.customProperty.behavior?.filters?.some((f) => {
          return (
            [RECURRING_REVENUE_CHANGE_TYPE.value, RECURRING_REVENUE_CHANGE_TYPE.value].includes(
              f.behavior?.behaviorType,
            ) || f.scdOptions?.type === `any`
          );
        });
      }
      // handles profile + rev filter
      return [RECURRING_REVENUE_CHANGE_TYPE.value, RECURRING_REVENUE_CHANGE_TYPE.value].includes(
        filter?.behavior?.behaviorType,
      );
    });
  });
}

export function getViewReplaysDisabledMessage(reason: ReplaysDisabledReason, t: LocalizationStore[`t`]): string | null {
  switch (reason) {
    case ReplaysDisabledReason.NOT_INTEGRATED:
      return t(`session-replay.integrate-replay`, {docsLink: `https://docs.mixpanel.com/docs/session-replay`});
    case ReplaysDisabledReason.COHORT_DISABLED:
      return t(`chart-actions.disabled.cohorts`);
    case ReplaysDisabledReason.RETENTION_EXCEEDED:
      return t(`chart-actions.disabled.replay-retention`, {retentionDays: REPLAY_RETENTION_DAYS});
    case ReplaysDisabledReason.FUNNEL_WITH_ATTRIBUTION:
      return t(`chart-actions.disabled.funnel-with-attribution`);
    default:
      return null;
  }
}
